/**
 * @file api-platform-shops.ts
 * @description API-клиент раздела «Магазины» кабинета платформы (phase19.28
 * этап А, v0.19.63; docs/business/platform.md PL1–PL17; макеты
 * docs/phases/phase19.28-materials/uiux-platform-shops.md).
 *
 * Магазины, их адреса (домены), журнал неизвестных адресов, интеграции по
 * магазину и перенос общей интеграции в магазин. Ответы — конверт
 * `{ data, error }`, `apiFetch` возвращает `data` и бросает `ApiError`.
 *
 * Поля, которых нет в утверждённом контракте, помечены «необязательно» и
 * читаются мягко: нет поля — нет колонки или строки, страница не падает.
 * Типы — по apps/api/src/platform-shops/platform-shops.types.ts: список,
 * домены и журнал неизвестных адресов — массивом; «Сделать главным» отдаёт
 * все адреса, удаление — `{ id }`; slug-check и owner-lookup; приглашение с
 * признаком доставки и ссылкой; договор с платформой; edge-config.
 */

import type { ShopDomainKind } from "@chaiberi/shared";

import { apiFetch } from "./api-client";
import type { ShopTimeZoneOption } from "./api-shop-settings";

/** Право раздела «Магазины» (меню, маршруты, создание и правка). */
export const SHOPS_PERMISSION = "PLATFORM_MANAGE_SHOPS";
/** Блокировка магазина (PL1, PL15). */
export const SHOPS_MODERATE_PERMISSION = "PLATFORM_MODERATE_SHOPS";
/** Вход в кабинет магазина под владельцем (PL17). */
export const IMPERSONATE_PERMISSION = "PLATFORM_IMPERSONATE_USER";

// ── Домены ──

export type ShopDomainStatus = "ACTIVE" | "DISABLED";

/** Последняя проверка адреса (только чтение, devops §5.3). */
export interface ShopDomainCheck {
  dns?: { ok: boolean; ips?: string[]; cname?: string | null; error?: string | null };
  /** `redirectsToHttps` — 301/308 на https: хост заведён с Force SSL. */
  proxy?: { ok: boolean; httpStatus?: number | null; redirectsToHttps?: boolean; error?: string | null };
  cert?: { ok: boolean; expiresAt?: string | null; san?: string[]; error?: string | null };
}

export interface ShopDomain {
  id: string;
  host: string;
  hostUnicode: string;
  kind: ShopDomainKind;
  isPrimary: boolean;
  status: ShopDomainStatus;
  managed: boolean;
  lastCheckAt: string | null;
  lastCheck: ShopDomainCheck | null;
  note: string | null;
  createdAt: string;
}

// ── Магазины ──

export interface ShopOwner {
  id: string;
  displayName: string | null;
  email: string;
}

export interface ShopListItem {
  id: string;
  slug: string;
  displayName: string;
  isActive: boolean;
  timeZone: string;
  primaryHost: string | null;
  /** Основной адрес для показа (`магазин.рф`). */
  primaryHostUnicode?: string | null;
  domainsCount: number;
  owners: ShopOwner[];
  createdAt: string;
  isClosed: boolean;
  /**
   * Последняя проверка основного адреса — колонки DNS/прокси/HTTPS; `null` —
   * не проверялся. Поля нет (старый API) — колонок нет.
   */
  primaryCheck?: { lastCheckAt: string | null; lastCheck: ShopDomainCheck | null } | null;
}

/** Готовность магазина к открытию (PL9) — для «Требует внимания». */
export interface ShopReadiness {
  orderPipeline: boolean;
  returnPipeline: boolean;
  legalProfile: boolean;
  privacyPolicy: boolean;
  paymentIntegration: boolean;
  fiscal: boolean;
  siteOpen: boolean;
  hasPublishedSite: boolean;
  /** Отметка о договоре с платформой (PL9, PL18). */
  contract?: boolean;
}

/** Заявка магазина на подключение своего домена. */
export interface ShopDomainRequest {
  host: string;
  hostUnicode: string;
  comment: string | null;
  requestedAt: string;
  requestedBy: { id: string; name: string } | null;
}

/** Отметка о договоре (оферте) Commora с магазином (PL9, PL18). */
export interface PlatformContract {
  signedAt: string;
  number: string | null;
  /** Кто отметил: имя/почта сотрудника платформы. */
  markedBy: { id: string; displayName: string | null; email: string } | null;
}

/** Итог отправки приглашения: `delivered: false` — письмо не ушло, есть `url`. */
export interface OwnerInvitationResult {
  sentTo: string;
  expiresAt: string;
  delivered?: boolean;
  url?: string;
}

export type OwnerInvitationStatus = "none" | "pending" | "accepted";

export interface ShopCard extends ShopListItem {
  contactEmail: string | null;
  readiness: ShopReadiness;
  domains: ShopDomain[];
  ownerInvitation: { status: OwnerInvitationStatus; expiresAt?: string; sentTo?: string };
  /** Заявки владельца на свой домен за 30 дней, новые сверху (PL11). */
  domainRequests?: ShopDomainRequest[];
  platformContract?: PlatformContract | null;
}

export type ShopOwnerInput = { mode: "existing"; userId: string } | { mode: "new"; email: string; displayName: string };

export interface CreateShopPayload {
  slug: string;
  displayName: string;
  timeZone?: string;
  contactEmail?: string;
  owner: ShopOwnerInput;
}

export interface CreateShopResult {
  shop: ShopCard;
  invitation?: OwnerInvitationResult;
  /** Сборка стартового сайта при создании (может не собраться — магазин всё равно создан). */
  siteStarter?: SiteStarterApplyResult;
}

/** Проверка slug при вводе. */
export interface ShopSlugCheck {
  slug: string;
  available: boolean;
  /** shop_slug_invalid | shop_slug_reserved | shop_slug_taken. */
  error: string | null;
  message: string | null;
  subdomainHost: string | null;
}

/** Поиск владельца по почте: найден / роль платформы / не найден. */
export interface OwnerLookup {
  email: string;
  user: ShopOwner | null;
  /** role_conflict — роль платформы; owner_inactive — пользователь отключён. */
  conflict: "role_conflict" | "owner_inactive" | null;
}

/** Итог сборки стартового сайта (создание магазина и «Собрать ещё раз»). */
export interface SiteStarterApplyResult {
  applied: boolean;
  reason: "already_applied" | "template_invalid" | "shop_not_found" | "failed" | "not_implemented" | null;
  pages?: Array<{ slug: string; status: "created" | "skipped" }>;
}

export interface UpdateShopPayload {
  displayName?: string;
  timeZone?: string;
  contactEmail?: string;
}

/** Адрес, на который пришли запросы, но он не привязан ни к одному магазину. */
export interface UnknownDomain {
  host: string;
  hostUnicode: string;
  hits: number;
  lastSeenDay: string;
}

// ── Интеграции магазина ──

export type IntegrationPolicy = "ONLY_OWN" | "SHARED_ALLOWED";
export type IntegrationScope = "SHOP" | "PLATFORM" | "NONE";

export interface ShopIntegrationItem {
  kind: string;
  title: string;
  policy: IntegrationPolicy;
  scope: IntegrationScope;
  integrationId: string | null;
  isActive: boolean;
  updatedAt: string | null;
}

const shopPath = (shopId: string) => `/admin/platform/shops/${encodeURIComponent(shopId)}`;
const domainPath = (shopId: string, domainId: string) => `${shopPath(shopId)}/domains/${encodeURIComponent(domainId)}`;

export const platformShops = {
  list: (q = "") =>
    apiFetch<ShopListItem[]>(`/admin/platform/shops${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`),
  /** Справочник поясов для кабинета платформы (формат как у `/admin/shop/time-zones`). */
  timeZones: () => apiFetch<ShopTimeZoneOption[]>("/admin/platform/time-zones"),
  slugCheck: (slug: string) =>
    apiFetch<ShopSlugCheck>(`/admin/platform/shops/slug-check?slug=${encodeURIComponent(slug)}`),
  ownerLookup: (email: string) =>
    apiFetch<OwnerLookup>(`/admin/platform/shops/owner-lookup?email=${encodeURIComponent(email.trim())}`),
  get: (shopId: string) => apiFetch<ShopCard>(shopPath(shopId)),
  create: (payload: CreateShopPayload) =>
    apiFetch<CreateShopResult>("/admin/platform/shops", { method: "POST", json: payload }),
  update: (shopId: string, payload: UpdateShopPayload) =>
    apiFetch<ShopCard>(shopPath(shopId), { method: "PATCH", json: payload }),
  setActive: (shopId: string, isActive: boolean) =>
    apiFetch<ShopCard>(`${shopPath(shopId)}/active`, { method: "PATCH", json: { isActive } }),
  resendInvitation: (shopId: string) =>
    apiFetch<OwnerInvitationResult>(`${shopPath(shopId)}/owner-invitation/resend`, { method: "POST" }),
  /** Отметка о договоре; `signedAt: null` — снять отметку. */
  setContract: (shopId: string, payload: { signedAt: string | null; number?: string | null }) =>
    apiFetch<ShopCard>(`${shopPath(shopId)}/contract`, { method: "PATCH", json: payload }),

  /** Собрать стартовый сайт ещё раз (идемпотентно, uiux-starter-site §7). */
  applySiteStarter: (shopId: string) =>
    apiFetch<SiteStarterApplyResult>(`${shopPath(shopId)}/site-starter/apply`, { method: "POST" }),

  listDomains: (shopId: string) =>
    apiFetch<ShopDomain[]>(`${shopPath(shopId)}/domains`),
  addDomain: (shopId: string, payload: { host: string; note?: string }) =>
    apiFetch<ShopDomain>(`${shopPath(shopId)}/domains`, { method: "POST", json: payload }),
  updateDomain: (shopId: string, domainId: string, payload: { status?: ShopDomainStatus; note?: string }) =>
    apiFetch<ShopDomain>(domainPath(shopId, domainId), { method: "PATCH", json: payload }),
  removeDomain: (shopId: string, domainId: string) =>
    apiFetch<{ id: string }>(domainPath(shopId, domainId), { method: "DELETE" }),
  makePrimary: (shopId: string, domainId: string) =>
    apiFetch<ShopDomain[]>(`${domainPath(shopId, domainId)}/make-primary`, { method: "POST" }),
  recheck: (shopId: string, domainId: string) =>
    apiFetch<ShopDomain>(`${domainPath(shopId, domainId)}/recheck`, { method: "POST" }),

  unknownDomains: (days = 7) =>
    apiFetch<UnknownDomain[]>(`/admin/platform/domains/unknown?days=${days}`),

  integrations: (shopId: string) => apiFetch<{ items: ShopIntegrationItem[] }>(`${shopPath(shopId)}/integrations`),
  transferIntegration: (integrationId: string, shopId: string) =>
    apiFetch<unknown>(`/admin/platform/integrations/${encodeURIComponent(integrationId)}/transfer`, {
      method: "POST",
      json: { shopId },
    }),
};

/** Адреса платформы для инструкции и проверок (`GET /admin/platform/edge-config`). */
export interface PlatformEdgeConfig {
  baseDomain: string;
  publicIps: string[];
  /** Куда вести CNAME `www`; null — на главный адрес магазина. */
  cnameTarget: string | null;
  npmGuidePath: string;
}

export const platformEdge = {
  config: () => apiFetch<PlatformEdgeConfig>("/admin/platform/edge-config"),
};
